/**
 * PATH: packages/ui/src/components/global-search/highlight.tsx
 * FILE_ID: UI-GLOBALSEARCH-HIGHLIGHT-001
 * PURPOSE: Highlight the matched query tokens inside a result string
 *          so the operator sees *why* a row matched ("micro-detailed").
 *          Pure I→O: (text, query) → React nodes with <mark>-styled
 *          spans around every case-insensitive token hit.
 * WHY:     Operator (2026-06-04): "microdetailed and completely
 *          optimised". A flat list with no visual link between the
 *          query and the row forces the eye to re-scan; highlighting
 *          the hit closes that loop instantly.
 * FLOW: (text, "60 hud") → ["…", <mark>60</mark>, " ", <mark>Hud</mark>, "son…"]
 * RELATED:
 *   - SearchResults.tsx: consumes highlightMatch for title/subtitle/desc
 */

import type { ReactNode } from 'react'

/** Escape a user token so it can't break the matching RegExp. */
function escapeRegExp(token: string): string {
  return token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Split a raw query into distinct, non-empty, lowercased tokens.
 * Quoted search operators (e.g. ``from:jake``) are split on the
 * colon so the value still highlights; pure whitespace collapses.
 * Deduped + sorted longest-first so overlapping tokens prefer the
 * longer match (avoids "hud" eating "hudson").
 */
export function tokenizeQuery(query: string): string[] {
  const raw = (query ?? '')
    .toLowerCase()
    .split(/[\s:]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
  const unique = Array.from(new Set(raw))
  unique.sort((a, b) => b.length - a.length)
  return unique
}

/**
 * Return `text` with every query-token occurrence wrapped in a
 * highlight `<mark>`. When there's nothing to highlight (empty
 * query or no hit) the original string is returned as a single
 * node, so the caller can always render the result directly.
 *
 * `keyPrefix` keeps React keys unique when the same util is used
 * for several fields in one row.
 */
export function highlightMatch(
  text: string | null | undefined,
  query: string,
  keyPrefix = 'h',
): ReactNode {
  const value = text ?? ''
  const tokens = tokenizeQuery(query)
  if (!value || tokens.length === 0) return value

  const pattern = new RegExp(`(${tokens.map(escapeRegExp).join('|')})`, 'gi')
  const parts = value.split(pattern)
  if (parts.length <= 1) return value

  const lowerTokens = new Set(tokens)
  return parts.map((part, i) => {
    if (part === '') return null
    if (lowerTokens.has(part.toLowerCase())) {
      return (
        <mark
          key={`${keyPrefix}-${i}`}
          className="bg-yellow-200/70 dark:bg-yellow-500/30 text-inherit rounded-[2px] px-0.5"
        >
          {part}
        </mark>
      )
    }
    return <span key={`${keyPrefix}-${i}`}>{part}</span>
  })
}
